<template>
  <section class="reviews-list" ref="root">
    <div class="reviews-list__inner" :class="{ 'reviews-list__inner_visible': inView }">
      <p v-if="error" class="reviews-list__error">{{ error }}</p>
      <div v-else-if="loading && !reviews.length" class="reviews-list__loading">
        <Loader size="lg" />
      </div>
      <div v-else class="reviews-list__grid">
        <article
          v-for="(review, i) in visibleReviews"
          :key="i"
          class="reviews-list__card"
          :style="{ transitionDelay: inView ? `${0.05 + (i % 5) * 0.06}s` : '0s' }"
        >
          <span class="reviews-list__quote-icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z"/></svg>
          </span>
          <blockquote class="reviews-list__quote">{{ review.quote }}</blockquote>
          <div
            v-if="review.imagePath"
            class="reviews-list__photo-wrap"
            @click="openPhoto(getReviewImageUrl(review.imagePath))"
          >
            <img
              :src="getReviewImageUrl(review.imagePath)"
              :alt="'Фото отзыва от ' + review.name"
              class="reviews-list__photo"
              loading="lazy"
            />
          </div>
          <footer class="reviews-list__author">
            <Avatar :name="review.name" :size="44" />
            <span class="reviews-list__author-text">
              <span class="reviews-list__name">{{ review.name }}</span>
              <span v-if="review.role" class="reviews-list__role">{{ review.role }}</span>
              <span v-if="review.date" class="reviews-list__date">{{ review.date }}</span>
            </span>
          </footer>
        </article>
      </div>
      <div v-if="hasMore" class="reviews-list__more">
        <button type="button" class="reviews-list__btn" @click="showMore">
          Показать ещё
        </button>
      </div>
    </div>

    <Teleport to="body">
      <Transition name="review-photo-modal">
        <div
          v-if="lightboxSrc"
          class="reviews-list__lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Фото отзыва"
          @click.self="lightboxSrc = null"
        >
          <button type="button" class="reviews-list__lightbox-close" aria-label="Закрыть" @click="lightboxSrc = null">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <div class="reviews-list__lightbox-content" @click.self="lightboxSrc = null">
            <img :src="lightboxSrc" alt="Фото отзыва" class="reviews-list__lightbox-img" />
          </div>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { getApiUrl } from '@/config/api'
import Avatar from '@/components/Avatar.vue'
import Loader from '@/components/Loader.vue'

const STEP = 5

export default {
  name: 'ReviewsList',
  components: { Avatar, Loader },
  setup() {
    const store = useStore()
    const root = ref(null)
    const inView = ref(false)
    const visibleCount = ref(STEP)
    let observer = null

    const reviews = computed(() => store.getters['reviews/items'] || [])
    const loading = computed(() => store.getters['reviews/loading'])
    const error = computed(() => store.getters['reviews/error'])
    const visibleReviews = computed(() => reviews.value.slice(0, visibleCount.value))
    const hasMore = computed(() => visibleCount.value < reviews.value.length)

    const lightboxSrc = ref(null)

    function getReviewImageUrl(imagePath) {
      if (!imagePath) return ''
      return getApiUrl(imagePath)
    }

    function openPhoto(src) {
      lightboxSrc.value = src
    }

    function showMore() {
      visibleCount.value = Math.min(visibleCount.value + STEP, reviews.value.length)
    }

    function onKeydown(e) {
      if (e.key === 'Escape') lightboxSrc.value = null
    }

    onMounted(() => {
      store.dispatch('reviews/fetchReviews')
      observer = new IntersectionObserver(
        ([e]) => { if (e.isIntersecting) inView.value = true },
        { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
      )
      if (root.value) observer.observe(root.value)
      window.addEventListener('keydown', onKeydown)
    })
    onUnmounted(() => {
      observer?.disconnect()
      window.removeEventListener('keydown', onKeydown)
    })

    return { root, inView, reviews, loading, error, visibleReviews, hasMore, showMore, lightboxSrc, getReviewImageUrl, openPhoto }
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/_variables.scss";

$dur: 0.5s;
$ease: cubic-bezier(0.22, 1, 0.36, 1);

.reviews-list {
  padding: 48px $gap 100px;
  background: $color-bg;

  @media (max-width: $breakpoint-mobile) {
    padding: 32px $gap 72px;
  }

  &__inner {
    max-width: $container-max;
    margin: 0 auto;

    &_visible .reviews-list__card { opacity: 1; transform: translateY(0); }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: $gap;
  }

  &__card {
    position: relative;
    padding: 28px 24px 24px;
    padding-top: 44px;
    background: $color-primary-inverse;
    border: 1px solid $color-border;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
    opacity: 0;
    transform: translateY(16px);
    transition: opacity $dur $ease, transform $dur $ease;

    &:hover {
      border-color: $color-accent-teal;
      box-shadow: 0 8px 24px rgba(13, 148, 136, 0.08);
    }
  }

  &__quote-icon {
    position: absolute;
    top: 20px;
    left: 24px;
    width: 28px;
    height: 28px;
    color: $color-accent-teal;
    opacity: 0.5;

    svg {
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  &__quote {
    margin: 0 0 20px;
    font-size: 16px;
    line-height: 1.55;
    color: $color-text;
    font-style: italic;
  }

  &__photo-wrap {
    margin-bottom: 16px;
    border-radius: 10px;
    overflow: hidden;
    background: $color-border;
    cursor: pointer;
    aspect-ratio: 4/3;
    max-height: 200px;

    &:hover .reviews-list__photo {
      transform: scale(1.03);
    }
  }

  &__photo {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.3s ease;
  }

  &__author {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__author-text {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 6px 8px;
  }

  &__name {
    font-weight: 700;
    font-size: 15px;
    color: $color-text;
  }

  &__role {
    font-size: 13px;
    color: $color-text-muted;
    font-weight: 500;
  }

  &__date {
    display: block;
    font-size: 12px;
    color: $color-text-muted;
    margin-top: 2px;
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    padding: 40px;
  }

  &__error {
    margin: 0 0 24px;
    font-size: 15px;
    color: #c53030;
  }

  &__more {
    margin-top: 40px;
    text-align: center;
  }

  &__lightbox {
    position: fixed;
    inset: 0;
    z-index: 3000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(0, 0, 0, 0.9);
    cursor: pointer;
  }

  &__lightbox-close {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 48px;
    height: 48px;
    padding: 0;
    border: none;
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
    &:hover { background: rgba(255, 255, 255, 0.2); }
    svg { width: 24px; height: 24px; }
  }

  &__lightbox-content {
    max-width: 90vw;
    max-height: 90vh;
    cursor: default;
  }

  &__lightbox-img {
    max-width: 100%;
    max-height: 85vh;
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 8px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  }

  &__btn {
    padding: 12px 32px;
    font-size: 15px;
    font-weight: 600;
    color: $color-primary-inverse;
    background: $color-accent-teal;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s, opacity 0.2s;

    &:hover {
      background: darken($color-accent-teal, 8%);
    }
    &:focus-visible {
      outline: 2px solid $color-accent-teal;
      outline-offset: 2px;
    }
  }
}

.review-photo-modal-enter-active,
.review-photo-modal-leave-active {
  transition: opacity 0.25s ease;
}
.review-photo-modal-enter-from,
.review-photo-modal-leave-to {
  opacity: 0;
}
.review-photo-modal-enter-active .reviews-list__lightbox-content,
.review-photo-modal-leave-active .reviews-list__lightbox-content {
  transition: transform 0.25s ease;
}
.review-photo-modal-enter-from .reviews-list__lightbox-content,
.review-photo-modal-leave-to .reviews-list__lightbox-content {
  transform: scale(0.95);
}
</style>
